import { pageLabel as sharedPageLabel } from "../../../components/pagination/pager.utils";
import { RoleModel } from "../../../factories/role/role.model";
import { WorkModel } from "../../../factories/works/models/work.model";

export type WorksTab = "active" | "my" | "queued" | "completed" | "archived";
export type StatFilter = "total" | "overdue" | "dueWeek" | "later";

export type BoardFilters = {
  clientIds: string[];
  userIds: string[];
  workTypeIds: string[];
  statuses: string[];
  roleIds: string[];
  /** Client team (Mg-231): Client owner / Client manager people ids or "me". */
  ownerIds: string[];
  managerIds: string[];
};

export const EMPTY_BOARD_FILTERS: BoardFilters = {
  clientIds: [],
  userIds: [],
  workTypeIds: [],
  statuses: [],
  roleIds: [],
  ownerIds: [],
  managerIds: [],
};

/** What a Work page tab remembers while a work item is open (Roadmap 1851). */
export type BoardMemory = {
  filters: BoardFilters;
  stat: StatFilter;
  queuedScope: "all" | "mine";
};

const STAT_FILTERS: StatFilter[] = ["total", "overdue", "dueWeek", "later"];

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];

/**
 * Starting state of a tab: the remembered state when coming Back (checked,
 * since it comes from storage), otherwise the link's presets (?userId= on
 * Active Work, ?status= from the Home pipeline) on empty filters.
 */
export const boardMemoryOrPreset = (
  tab: WorksTab,
  remembered: unknown,
  presets: { userId?: string | null; status?: string | null },
): BoardMemory => {
  if (remembered && typeof remembered === "object") {
    const saved = remembered as Partial<BoardMemory>;
    const filters = (saved.filters ?? {}) as Partial<BoardFilters>;
    return {
      filters: {
        clientIds: stringList(filters.clientIds),
        userIds: stringList(filters.userIds),
        workTypeIds: stringList(filters.workTypeIds),
        statuses: stringList(filters.statuses),
        roleIds: stringList(filters.roleIds),
        ownerIds: stringList(filters.ownerIds),
        managerIds: stringList(filters.managerIds),
      },
      stat: STAT_FILTERS.includes(saved.stat as StatFilter)
        ? (saved.stat as StatFilter)
        : "total",
      queuedScope: saved.queuedScope === "mine" ? "mine" : "all",
    };
  }
  return {
    filters: {
      ...EMPTY_BOARD_FILTERS,
      ...(presets.userId && tab === "active"
        ? { userIds: [presets.userId] }
        : {}),
      ...(presets.status ? { statuses: [presets.status] } : {}),
    },
    stat: "total",
    queuedScope: "all",
  };
};

/**
 * The address's ?status= / ?userId= follow the filters (QA batch 12): after
 * "Clear all" or picking another (sub-)status the address kept ?status=Ready,
 * so a reload or a shared link showed the old filter. One picked status /
 * person (person: Active tab only, like the preset) = in the address;
 * none or several = removed.
 */
export const presetParamsFor = (
  tab: WorksTab,
  filters: Pick<BoardFilters, "statuses" | "userIds">,
): { status: string | null; userId: string | null } => ({
  status: filters.statuses.length === 1 ? filters.statuses[0] : null,
  userId:
    tab === "active" && filters.userIds.length === 1
      ? filters.userIds[0]
      : null,
});

export const WORKS_TABS: {
  key: WorksTab;
  label: string;
  path: string;
  tip: string;
}[] = [
  {
    key: "active",
    label: "Active Work",
    path: "/works/all",
    tip: "All open work across the whole firm — every status except completed.",
  },
  {
    key: "my",
    label: "My Work",
    path: "/works/my",
    tip: "Work assigned to you, including work you picked up for one of your roles.",
  },
  {
    key: "queued",
    label: "Queued",
    path: "/works/queued",
    tip: "Work assigned to a role, but no one has taken ownership yet. Once someone in that role picks it up, it leaves the queue.",
  },
  {
    key: "completed",
    label: "Completed",
    path: "/works/completed",
    tip: "Work with a completed (green) status.",
  },
  {
    key: "archived",
    label: "Archived",
    path: "/works/archived",
    tip: "Deleted or archived work. You can reactivate it at any time.",
  },
];

/** Which works a tab shows (on top of the user's own filters). */
export const tabQuery = (tab: WorksTab, userId: string) => {
  switch (tab) {
    case "my":
      return { userIds: [userId], hideGreenStatus: true };
    case "queued":
      return { isQueued: true, hideGreenStatus: true };
    case "completed":
      return { workStatuses: ["Completed"], hideGreenStatus: false };
    case "archived":
      return { isArchived: true, hideGreenStatus: false };
    default:
      return { hideGreenStatus: true };
  }
};

export const tabHasStats = (tab: WorksTab) => tab === "active" || tab === "my";

const startOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

const endOfDay = (date: Date) => {
  const day = new Date(date);
  day.setHours(23, 59, 59, 999);
  return day;
};

/**
 * Due date ranges for the stat cards. A week ends on Sunday: "due this week"
 * is today until Sunday, "overdue" is before today, "due later" after Sunday.
 */
export const statDueRange = (
  stat: StatFilter,
  today: Date = new Date(),
): { fromDate: Date; toDate: Date } | null => {
  const todayStart = startOfDay(today);
  const sunday = new Date(todayStart);
  sunday.setDate(sunday.getDate() + ((7 - sunday.getDay()) % 7));
  const sundayEnd = endOfDay(sunday);

  if (stat === "overdue") {
    return {
      fromDate: new Date(2000, 0, 1),
      toDate: new Date(todayStart.getTime() - 1),
    };
  }
  if (stat === "dueWeek") return { fromDate: todayStart, toDate: sundayEnd };
  if (stat === "later") {
    return {
      fromDate: new Date(sundayEnd.getTime() + 1),
      toDate: new Date(2100, 0, 1),
    };
  }
  return null;
};

/** The roles the user belongs to. */
export const myRoleIds = (
  roles: RoleModel[] | undefined,
  userId: string | undefined,
) =>
  (roles ?? [])
    .filter((role) =>
      (role.userRoles ?? []).some(
        (member) =>
          !!userId && member.userId?.toLowerCase() === userId.toLowerCase(),
      ),
    )
    .map((role) => role.id.toLowerCase());

/** Queued works narrowed to the picked roles and, for "My queued", my roles. */
export const filterQueuedWorks = (
  works: WorkModel[],
  pickedRoleIds: string[],
  mineRoleIds: string[] | null,
) => {
  const picked = new Set(pickedRoleIds.map((id) => id.toLowerCase()));
  const mine = mineRoleIds ? new Set(mineRoleIds) : null;
  return works.filter((work) => {
    const roleId = (work.roleId || "").toLowerCase();
    if (picked.size && !picked.has(roleId)) return false;
    if (mine && !mine.has(roleId)) return false;
    return true;
  });
};

/** "Showing 21–40 of 510" (shared pager rule, Roadmap 1867). */
export const pageLabel = (page: number, pageSize: number, total: number) =>
  sharedPageLabel(page, pageSize, total, "No work items");

export type StatusMenuItem = {
  label: string;
  color?: string;
  /** Main status group (Ready, In Progress, Waiting, Completed). */
  isGroup: boolean;
  group: string;
  count?: number;
};

// The owner's order: blue, yellow, red, green; other groups (e.g. a grey
// "Scheduled") after them.
const GROUP_ORDER = ["ready", "in progress", "waiting", "completed"];
const groupRank = (name: string) => {
  const rank = GROUP_ORDER.indexOf(name.trim().toLowerCase());
  return rank < 0 ? GROUP_ORDER.length : rank;
};

const sameText = (a?: string | null, b?: string | null) =>
  (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

/**
 * The "Select status" list (owner, Roadmap 1833): main groups in their own
 * order (Ready blue, In Progress yellow, Waiting red, Completed green), each
 * followed by its sub-statuses A–Z (one row per name), and, when works are
 * given, how many of them are in each status.
 */
export const statusMenuItems = (
  statuses: { label: string; color?: string; isBold?: boolean }[],
  works?: Pick<WorkModel, "jobStatus" | "jobSubStatus">[],
): StatusMenuItem[] => {
  const groups: { head: StatusMenuItem; subs: StatusMenuItem[] }[] = [];
  statuses.forEach((status) => {
    if (status.isBold || !groups.length) {
      groups.push({
        head: {
          label: status.label,
          color: status.color,
          isGroup: true,
          group: status.label,
        },
        subs: [],
      });
      return;
    }
    const current = groups[groups.length - 1];
    if (
      !status.label?.trim() ||
      current.subs.some((sub) => sameText(sub.label, status.label))
    )
      return;
    current.subs.push({
      label: status.label,
      color: status.color,
      isGroup: false,
      group: current.head.label,
    });
  });

  // Sub-statuses that work items use but the work-type list doesn't offer
  // (e.g. renamed or removed later) still get a row, so that work can be
  // found (QA batch 16, WORK-021).
  (works ?? []).forEach((work) => {
    const groupName = (work.jobStatus || "").trim();
    if (!groupName) return;
    let group = groups.find((g) => sameText(g.head.label, groupName));
    if (!group) {
      group = {
        head: { label: groupName, isGroup: true, group: groupName },
        subs: [],
      };
      groups.push(group);
    }
    const sub = (work.jobSubStatus || "").trim();
    if (!sub || group.subs.some((item) => sameText(item.label, sub))) return;
    group.subs.push({
      label: sub,
      color: group.head.color,
      isGroup: false,
      group: group.head.label,
    });
  });

  const countOf = (item: StatusMenuItem) =>
    works?.filter(
      (work) =>
        sameText(work.jobStatus, item.group) &&
        (item.isGroup || sameText(work.jobSubStatus, item.label)),
    ).length;

  const ordered = groups
    .map((group, index) => ({ group, index }))
    .sort(
      (a, b) =>
        groupRank(a.group.head.label) - groupRank(b.group.head.label) ||
        a.index - b.index,
    )
    .map(({ group }) => group);

  return ordered.flatMap(({ head, subs }) =>
    [
      head,
      ...[...subs].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      ),
    ].map((item) => (works ? { ...item, count: countOf(item) } : item)),
  );
};

/**
 * The status names to send to find-work: a picked main group also sends its
 * sub-statuses, because the server only matches a group name on work WITHOUT
 * a sub-status (QA batch 16, WORK-021: "In Progress 41" listed 29).
 */
export const expandStatusFilter = (
  picked: string[],
  items: StatusMenuItem[],
): string[] => {
  const out: string[] = [];
  const add = (name: string) => {
    if (!out.some((existing) => sameText(existing, name))) out.push(name);
  };
  picked.forEach((name) => {
    add(name);
    items
      .filter((item) => !item.isGroup && sameText(item.group, name))
      .forEach((item) => add(item.label));
  });
  return out;
};

/** Does the work match the picked statuses (group or sub-status names)? */
export const matchesStatuses = (
  work: Pick<WorkModel, "jobStatus" | "jobSubStatus">,
  statuses: string[],
) =>
  !statuses.length ||
  statuses.some(
    (status) =>
      sameText(work.jobStatus, status) || sameText(work.jobSubStatus, status),
  );

/** Number of works per work type id (lower case). */
export const workTypeCounts = (works: Pick<WorkModel, "workTypeId">[]) => {
  const counts = new Map<string, number>();
  works.forEach((work) => {
    const id = (work.workTypeId || "").toLowerCase();
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  });
  return counts;
};

/** Queued tab "Select Role" counts: work items per role id (lower case). */
export const roleCounts = (works: Pick<WorkModel, "roleId">[]) => {
  const counts = new Map<string, number>();
  works.forEach((work) => {
    const id = (work.roleId || "").toLowerCase();
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  });
  return counts;
};

export type StatusChangeItem = {
  key: string;
  label: string;
  color?: string;
  /** Main status (bold, at the left); false = sub-status (indented). */
  isGroup: boolean;
  statusId: string;
  subStatusId: string | null;
};

/**
 * A work type's statuses for a single-pick status changer that looks like the
 * Work page "Select status" list (Roadmap 1858): main statuses in the owner's
 * order (Ready, In Progress, Waiting, Completed, then others such as
 * Scheduled), each followed by its own sub-statuses A–Z.
 */
export const statusChangeItems = (
  statuses: {
    id: string;
    name?: string | null;
    color?: string | null;
    serviceSubStatuses?: { id: string; subStatus?: string | null }[] | null;
  }[],
): StatusChangeItem[] =>
  statuses
    .filter((status) => status?.name && status.name !== "trial")
    .map((status, index) => ({ status, index }))
    .sort(
      (a, b) =>
        groupRank(a.status.name || "") - groupRank(b.status.name || "") ||
        a.index - b.index,
    )
    .flatMap(({ status }) => [
      {
        key: status.id,
        label: status.name || "",
        color: status.color || undefined,
        isGroup: true,
        statusId: status.id,
        subStatusId: null,
      },
      ...(status.serviceSubStatuses ?? [])
        .filter((sub) => sub?.subStatus?.trim())
        .sort((a, b) =>
          (a.subStatus || "").localeCompare(b.subStatus || "", undefined, {
            sensitivity: "base",
          }),
        )
        .map((sub) => ({
          key: `${status.id}:${sub.id}`,
          label: sub.subStatus || "",
          color: status.color || undefined,
          isGroup: false,
          statusId: status.id,
          subStatusId: sub.id,
        })),
    ]);

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/** An id the server uses for "none": missing, "" or the all-zero GUID. */
const realId = (id?: string | null) => {
  const value = (id || "").trim().toLowerCase();
  return value && value !== EMPTY_GUID ? value : "";
};

/**
 * True when a status menu row is the work's status now (gets the ✓). The
 * server sends an empty or all-zero sub-status id when there is none (QA D1),
 * which means "the main status itself".
 */
export const isCurrentStatusItem = (
  item: { statusId: string | null; subStatusId: string | null },
  current?: { statusId?: string | null; subStatusId?: string | null },
) =>
  !!realId(current?.statusId) &&
  realId(current?.statusId) === realId(item.statusId) &&
  realId(current?.subStatusId) === realId(item.subStatusId);

export type CurrentStatus = {
  statusId?: string | null;
  subStatusId?: string | null;
  /** Names as find-work sends them (jobStatus / jobSubStatus). */
  statusName?: string | null;
  subStatusName?: string | null;
};

const sameName = (a?: string | null, b?: string | null) =>
  !!(a || "").trim() &&
  (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

/**
 * The key of the status menu row that is the work's status now (gets the ✓),
 * or null (QA batch 10). find-work rows don't always carry ids that match the
 * work type's statuses: jobSubStatusId is never filled (only the name), and
 * older work can point at a status id of another copy of the work type. So:
 * main status by id, else by name; then the sub-status under it by id, else by
 * name; no sub-status = the main status itself.
 */
export const currentStatusItemKey = (
  items: StatusChangeItem[],
  current?: CurrentStatus,
): string | null => {
  if (!current) return null;
  const groups = items.filter((item) => item.isGroup);
  const statusId = realId(current.statusId);
  const group =
    (statusId && groups.find((item) => realId(item.statusId) === statusId)) ||
    groups.find((item) => sameName(item.label, current.statusName));
  const subId = realId(current.subStatusId);
  const subName = (current.subStatusName || "").trim();
  const subs = items.filter((item) => !item.isGroup);
  if (subId) {
    const byId = subs.find((item) => realId(item.subStatusId) === subId);
    if (byId) return byId.key;
  }
  if (!group) return null;
  const ownSubs = subs.filter((item) => item.statusId === group.statusId);
  if (subName) {
    const byName = ownSubs.find((item) => sameName(item.label, subName));
    return byName ? byName.key : null;
  }
  return subId ? null : group.key;
};

/**
 * The work's status now when it isn't one of its work type's statuses (QA
 * batch 11, e.g. "finished (Waiting)" after the work type changed): the label
 * the status menu shows at the top as the ticked, greyed row. null when the
 * status is in the list (or unknown).
 */
export const currentStatusOutsideList = (
  items: StatusChangeItem[],
  current?: CurrentStatus,
): string | null => {
  if (!current || !items.length) return null;
  const statusName = (current.statusName || "").trim();
  const subName = (current.subStatusName || "").trim();
  if (!statusName && !subName) return null;
  if (currentStatusItemKey(items, current)) return null;
  if (subName && statusName) return `${subName} (${statusName})`;
  return subName || statusName;
};

/**
 * Grey text after a client in the Work page client filter (QA batch 16,
 * WORK-026): "Individual" / "Organization", plus — when another client in the
 * list has the same name — its email, else its phone (QA batch 17: the search
 * gives no city), else a short id "#a1b2c3" so the rows never look identical.
 */
export const clientOptionDetail = (
  option: {
    label: string;
    avatarColor?: string;
    email?: string;
    phone?: string;
    value?: string | number;
  },
  all: { label: string }[],
) => {
  const kind = option.avatarColor === "purple" ? "Organization" : "Individual";
  const isNameShared =
    all.filter((other) => sameText(other.label, option.label)).length > 1;
  if (!isNameShared) return kind;
  const extra =
    option.email?.trim() ||
    option.phone?.trim() ||
    (option.value
      ? `#${String(option.value).replace(/-/g, "").slice(0, 6)}`
      : "");
  return extra ? `${kind} · ${extra}` : kind;
};

/** Ticks / unticks one work item in a selection (Work page, Home Needs attention). */
export const toggleSelectedWork = (
  selected: WorkModel[],
  work: WorkModel,
): WorkModel[] =>
  selected.some((item) => item.workId === work.workId)
    ? selected.filter((item) => item.workId !== work.workId)
    : [...selected, work];

/** True when every listed work item is ticked (header checkbox). */
export const allWorksSelected = (
  works: WorkModel[],
  selected: WorkModel[],
): boolean => {
  const ids = new Set(selected.map((work) => work.workId));
  return !!works.length && works.every((work) => ids.has(work.workId));
};

/** Header checkbox: tick every listed item, or untick all when all are ticked. */
export const toggleAllWorks = (
  works: WorkModel[],
  selected: WorkModel[],
): WorkModel[] => (allWorksSelected(works, selected) ? [] : [...works]);

/**
 * Keeps only the ticked items that are still listed (Home refreshes its lists
 * after a change; an item that left the list must not stay selected unseen).
 */
export const keepListedSelection = (
  selected: WorkModel[],
  works: WorkModel[] | undefined,
): WorkModel[] => {
  const ids = new Set((works ?? []).map((work) => work.workId));
  const kept = selected.filter((work) => ids.has(work.workId));
  return kept.length === selected.length ? selected : kept;
};
